import jsPDF from 'jspdf';
import {
  exportConcreteClassReport,
  getLG14Report,
} from '../../../../../services/api/concreteClasses';
import AccionaRegular from '../../../../../style/fonts/acciona-regular-normal';
import AccionaBold from '../../../../../style/fonts/acciona-medium-bold';
import logo from '../../../../../style/img/logo_acciona.png';
import dayjs from 'dayjs';

const sharedHeadStyles = {
  fillColor: '#ececec',
  textColor: '#000000',
  fontSize: 6,
  halign: 'center',
  font: 'AccionaBold',
  valign: 'middle',
};

const sharedBodyStyles = {
  fontSize: 7,
  halign: 'center',
  font: 'AccionaRegular',
  valign: 'middle',
};

const commonTableStyles = {
  theme: 'grid',
  margin: { top: 50, left: 20, right: 20 },
  headStyles: sharedHeadStyles,
  bodyStyles: sharedBodyStyles,
};

const addDocumentHeader = (doc, pageWidth) => {
  doc.addImage(logo, 'png', 20, 20, 140, 67);

  doc.setFont('AccionaRegular', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(0, 0, 0);
  const today = dayjs().format('DD/MM/YYYY HH:mm:ss');
  const dateWidth = doc.getTextWidth(today);
  doc.text(today, pageWidth - dateWidth - 20, 30);
};

const generateLG14PDF = async ({ type, project }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const normative = project.normative_data.name;
      const report = await getLG14Report(type, project.id_project);

      const { classes, deliveries_by_case } = report.data;

      // Aplanar todos los albaranes de todos los casos en un único array
      const deliveries = Object.values(deliveries_by_case).flat();

      const doc = new jsPDF({
        unit: 'px',
        hotfixes: ['px_scaling'],
        compress: true,
      });

      doc.addFileToVFS('AccionaRegular.ttf', AccionaRegular);
      doc.addFileToVFS('AccionaBold.ttf', AccionaBold);
      doc.addFont('AccionaRegular.ttf', 'AccionaRegular', 'normal');
      doc.addFont('AccionaBold.ttf', 'AccionaBold', 'bold');

      const pageWidth = doc.internal.pageSize.getWidth();

      const addProjectInfoTable = () => {
        doc.autoTable({
          head: [['Código de obra', 'Edificio', 'Ref. catastral']],
          body: [
            [project.work_code, project.name, project.catastral_reference],
          ],
          ...commonTableStyles,
          startY: 90,
        });
      };
      const addTitleTable = () => {
        doc.autoTable({
          body: [['LG 14 ', '2. Control de recepción de productos', '']],
          theme: 'plain',
          margin: { top: 50, left: 20, right: 20 },
          bodyStyles: {
            halign: 'center',
            font: 'AccionaBold',
            fontSize: 9,
          },
          startY: doc.lastAutoTable.finalY + 10,
        });

        const lastTable = doc.lastAutoTable;
        if (lastTable && lastTable.body.length > 0) {
          const firstCell = lastTable.body[0].cells['0'];
          const lastCell =
            lastTable.body[lastTable.body.length - 1].cells[
              (lastTable.columns.length - 1).toString()
            ];

          const x = firstCell.x;
          const y = firstCell.y;
          const width = lastCell.x + lastCell.width - x;
          const height = lastCell.y + lastCell.height - y;

          doc.setLineWidth(0.5);
          doc.setDrawColor(200);
          doc.rect(x, y, width, height);
        }
      };

      const addConcreteTypesTable = () => {
        const normativeText = normative === 'EHE' ? 'EHE-08' : 'CE';

        doc.setFontSize(12);
        doc.text(
          `2.9 Control documental y experimental de hormigón según ${normativeText}.`,
          20,
          doc.lastAutoTable.finalY + 50,
        );

        doc.autoTable({
          head: [
            [
              'Tipo de hormigón en obra',
              'Tipificiación',
              'Modalidad de control',
              'Suministrador hormigón',
              'Tipo de cemento',
              'Con distintivo',
            ],
          ],
          body: classes.map((item) => [
            item.name,
            item.recipe,
            item.control_type,
            item.plant,
            item.concrete_list,
            item.concrete_quality ? 'Sí' : 'No',
          ]),
          ...commonTableStyles,
          headStyles: {
            ...commonTableStyles.headStyles,
          },
          startY: doc.lastAutoTable.finalY + 70,
        });
      };

      // Genera las filas de la tabla para un array de albaranes ya filtrado por caso
      const generateStatisticalControlRows = (caseDeliveries, caseNumber) => {
        const tableRows = [];
        let previousBatchCode = null;

        const isCase1 = caseNumber === '1';
        const isCase0 = caseNumber === '0';

        caseDeliveries.forEach((note) => {
          if (
            type === 'statistical' &&
            previousBatchCode !== null &&
            previousBatchCode !== note.batch_code
          ) {
            tableRows.push([
              {
                content: '',
                colSpan: 13,
                styles: {
                  fillColor: '#fcfcfc',
                  minCellHeight: 5,
                },
              },
            ]);
          }
          tableRows.push([
            ...(type === 'statistical'
              ? [note.batch_code, note.detailed_location]
              : []),
            note.docket_number,
            note.reference_number,
            ...(type === '100' ? [note.detailed_location] : []),
            note.arriving_date,
            note.slump,
            ...(type === '100'
              ? [
                  note.xi_28?.toFixed(2),
                  note.fck?.toFixed(2),
                  note.fcreal?.toFixed(2),
                ]
              : isCase0
              ? [
                  note.min_ave_mash_strength?.toFixed(2),
                  `${note?.concrete_value?.toFixed(2)} / ${(
                    (note.concrete_value || 0) * 0.9
                  ).toFixed(2)}`,
                ]
              : isCase1
              ? [
                  note.ave_mash_strength?.toFixed(2) ||
                    note.min_ave_mash_strength?.toFixed(2),
                  note.delta,
                  note.acceptance_value?.toFixed(2),
                  `${note?.concrete_value?.toFixed(2)} / ${(
                    (note.concrete_value || 0) * 0.9
                  ).toFixed(2)}`,
                ]
              : normative === 'EHE'
              ? [
                  note.xi_28?.toFixed(2),
                  caseNumber === '3'
                    ? note.k3_mash?.toFixed(2)
                    : note.k2_mash?.toFixed(2),
                ]
              : caseNumber === '3'
              ? [note.x_x1?.toFixed(2), note.kn_mash?.toFixed(2)]
              : [note.x_x1?.toFixed(2), note.s35_mash?.toFixed(2)]),
            ...(isCase1 || isCase0 ? [] : [note.fx?.toFixed(2)]),
            note.aceptation_date,
            note.compliance,
          ]);

          previousBatchCode = note.batch_code;
        });

        return tableRows;
      };

      // Cabeceras de columna según el caso
      const getTableHeaders = (caseNumber) => {
        const isCase0 = caseNumber === '0';
        const isCase1 = caseNumber === '1';
        return [
          ...(type === 'statistical' ? ['Lote', 'Elemento estructural'] : []),
          'Albarán',
          'Toma',
          ...(type === '100' ? ['Elemento estructural'] : []),
          'Fecha Muestreo',
          'Consistencia (mm)',
          ...(type === '100'
            ? ['Xi 28', 'Fck', 'Fc Real']
            : isCase0
            ? ['X1', 'fck / fck 90%']
            : isCase1
            ? ['X', 'Delta', 'f(x̅)', 'fck / fck 90%']
            : normative === 'EHE'
            ? ['Xi 28', caseNumber === '3' ? 'K3' : 'K2']
            : caseNumber === '3'
            ? ['x/x1', 'Kn']
            : ['x̅', 'S35']),
          ...(isCase1 || isCase0 ? [] : ['f(x̅)']),
          'Fecha Aceptación',
          'Observación',
        ];
      };

      const addStatisticalControlTable = () => {
        const uniqueClasses = [
          ...new Set(deliveries.map((d) => d.class_name)),
        ].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

        uniqueClasses.forEach((className) => {
          // Construir la lista de casos con sus albaranes para esta clase
          const casesForClass = Object.entries(deliveries_by_case)
            .filter(([caseKey]) => caseKey.startsWith('case'))
            .flatMap(([caseKey, caseDeliveries]) => {
              const filtered = caseDeliveries.filter(
                (d) => d.class_name === className,
              );
              if (filtered.length === 0) return [];

              // Auto-detección: si hay albaranes con k3_mash en case2 (EHE), dividir en caso 2 y caso 3
              if (caseKey === 'case2' && normative === 'EHE') {
                const case2 = filtered.filter((d) => d.k2_mash !== null);
                const case3 = filtered.filter(
                  (d) => d.k2_mash === null && d.k3_mash !== null,
                );
                const result = [];
                if (case2.length > 0)
                  result.push({ caseNumber: '2', deliveries: case2 });
                if (case3.length > 0)
                  result.push({ caseNumber: '3', deliveries: case3 });
                return result;
              }

              return [
                {
                  caseNumber: caseKey.replace('case', ''),
                  deliveries: filtered,
                },
              ];
            })
            .filter((c) => c.deliveries.length > 0)
            .sort((a, b) => {
              const numA = parseInt(a.caseNumber);
              const numB = parseInt(b.caseNumber);
              if (normative === 'EHE') {
                return numA - numB;
              } else {
                const coeOrder = { 0: 0, 1: 1, 3: 2, 2: 3 };
                return (
                  (coeOrder[a.caseNumber] ?? numA) -
                  (coeOrder[b.caseNumber] ?? numB)
                );
              }
            });

          casesForClass.forEach((caseInfo) => {
            doc.addPage();

            const { caseNumber } = caseInfo;
            const tableRows = generateStatisticalControlRows(
              caseInfo.deliveries,
              caseNumber,
            );

            doc.autoTable({
              head: [getTableHeaders(caseNumber)],
              body: tableRows,
              ...commonTableStyles,
              startY: 185,
              margin: { top: 90, left: 20, right: 20 },
              showHead: 'everyPage',
              didDrawPage: (data) => {
                addDocumentHeader(doc, pageWidth);

                if (data.pageNumber === 1) {
                  let y = 110;
                  if (type === 'statistical') {
                    doc.setFont('AccionaRegular', 'normal');
                    doc.setFontSize(12);
                    doc.text(
                      '2.9.1 Control estadístico de la resistencia.',
                      20,
                      y,
                    );
                    doc.setFontSize(7);
                    doc.text(
                      normative === 'EHE'
                        ? '(TAMAÑO DEL LOTE: según art. 86.5.4.1 de la Instrucción EHE-08)'
                        : '(TAMAÑO DEL LOTE: según art. 57.5.4.1 de la Instrucción CE)',
                      20,
                      y + 15,
                    );
                    y += 30;
                  } else {
                    doc.setFont('AccionaRegular', 'normal');
                    doc.setFontSize(12);
                    doc.text('2.9.2 Control 100% de la resistencia.', 20, y);
                  }

                  doc.setFontSize(10);
                  doc.setFont('AccionaBold', 'bold');
                  doc.text(className, 20, y + 20);

                  doc.setFont('AccionaRegular', 'normal');
                  doc.text(`CASO ${caseNumber}`, 20, y + 35);
                }
              },
            });

            const pageHeight = doc.internal.pageSize.getHeight();
            const tableEndY = doc.lastAutoTable.finalY;

            const observationsHeight = 150 + 40;
            const signaturesHeight = 120;
            const footerSpace = 50;
            const totalFooterHeight =
              observationsHeight + signaturesHeight + footerSpace + 40;

            const hasSpaceAtBottom =
              pageHeight - tableEndY >= totalFooterHeight;
            const observationsStartY = hasSpaceAtBottom
              ? pageHeight - totalFooterHeight + 20
              : tableEndY + 70;

            doc.autoTable({
              head: [['Observaciones:']],
              body: [['']],
              theme: 'grid',
              pageBreak: 'avoid',
              headStyles: {
                ...sharedHeadStyles,
                halign: 'left',
              },
              margin: { top: 50, left: 20, right: 20 },
              bodyStyles: {
                ...sharedBodyStyles,
                halign: 'left',
                valign: 'top',
                minCellHeight: 150,
              },
              startY: observationsStartY,
            });

            doc.autoTable({
              head: [
                [
                  'Director/a de Ejecición de Obra',
                  'Laboratorio',
                  'Empresa Constructora',
                ],
              ],
              body: [
                [
                  { content: '', styles: { minCellHeight: 80 } },
                  { content: '', styles: { minCellHeight: 80 } },
                  { content: '', styles: { minCellHeight: 80 } },
                ],
                [
                  {
                    styles: { halign: 'right', font: 'AccionaRegular' },
                    content: 'Firma',
                  },
                  {
                    styles: { halign: 'right', font: 'AccionaRegular' },
                    content: 'Sello y Firma',
                  },
                  {
                    styles: { halign: 'right', font: 'AccionaRegular' },
                    content: 'Sello y Firma',
                  },
                ],
              ],
              theme: 'grid',
              headStyles: {
                ...sharedHeadStyles,
                halign: 'center',
              },
              margin: { top: 50, left: 20, right: 20 },
              bodyStyles: {
                ...sharedBodyStyles,
                halign: 'left',
                valign: 'top',
              },
              startY: doc.lastAutoTable.finalY + 20,
              pageBreak: 'avoid',
            });
          });
        });
      };

      addDocumentHeader(doc, pageWidth);
      addProjectInfoTable();
      addTitleTable();
      addConcreteTypesTable();
      addStatisticalControlTable();

      const pageCount = doc.internal.getNumberOfPages();
      doc.setFont('AccionaRegular', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(128, 128, 128);

      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const pageText = `${i} de ${pageCount}`;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const textWidth = doc.getTextWidth(pageText);

        doc.text(pageText, pageWidth / 2 - textWidth / 2, pageHeight - 30);
      }

      doc.save(
        `${project.work_code}_${
          type === '100' ? 'C100' : normative.toUpperCase()
        }_LG14_${dayjs().format('YYYYMMDD')}.pdf`,
      );
      resolve();
    } catch (error) {
      console.error('Error generating LG14 PDF:', error);
      reject(error);
    }
  });
};

const generateConcreteClassPDF = async (
  { concreteClass, project },
  onlyControlMash = false,
  type = 'statistical',
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await exportConcreteClassReport(
        {
          id_concrete_class: concreteClass.id_concrete_class,
          name: concreteClass.name,
          control_type: concreteClass.control_type,
          plant: concreteClass.plant,
          recipe: concreteClass.recipe,
          concrete_quality: concreteClass.concrete_quality,
          certified_dis: concreteClass.certified_dis || false,
        },
        project.id_project,
      );
      const normative = project.normative_data.name;

      const doc = new jsPDF({
        unit: 'px',
        hotfixes: ['px_scaling'],
        compress: true,
        orientation: 'landscape',
      });

      doc.addFileToVFS('AccionaRegular.ttf', AccionaRegular);
      doc.addFileToVFS('AccionaBold.ttf', AccionaBold);
      doc.addFont('AccionaRegular.ttf', 'AccionaRegular', 'normal');
      doc.addFont('AccionaBold.ttf', 'AccionaBold', 'bold');

      const pageWidth = doc.internal.pageSize.getWidth();
      const caseKeys = Object.keys(data)
        .filter((k) => k !== 'class')
        .filter((k) => data[k].length)
        .sort((a, b) => {
          const numA = parseInt(a.replace('case', ''));
          const numB = parseInt(b.replace('case', ''));
          if (normative === 'EHE') {
            return numA - numB;
          } else {
            const coeOrder = { case0: 0, case1: 1, case3: 2, case2: 3 };
            return (coeOrder[a] ?? numA) - (coeOrder[b] ?? numB);
          }
        });
      const firstCaseKey = caseKeys[0];
      const firstCase = data[firstCaseKey]?.[0];

      const addTitleTable = () => {
        doc.autoTable({
          head: [
            [
              'Clase',
              'Centro',
              'Código de Obra',
              'Planta',
              'Tipo de hormigón',
              'FCK',
              ...(type === 'statistical'
                ? ['Número mínimo amasadas a controlar (M)']
                : [
                    'No Amasadas (N)',
                    'Posición 0,05*N (n)',
                    'Fc Real',
                    'Cumple Resistencia',
                    'Cumple Resistencia (90%)',
                  ]),
            ],
          ],
          theme: 'grid',
          margin: { top: 50, left: 20, right: 20 },
          ...commonTableStyles,
          body: [
            [
              concreteClass.name,
              project.name,
              project.work_code,
              concreteClass.plant,
              concreteClass.recipe,
              firstCase?.concrete_value,
              ...(type === 'statistical'
                ? [firstCase?.min_n_control_mash]
                : [
                    firstCase?.total_control_mash,
                    data?.class?.position_n,
                    firstCase?.fcreal?.toFixed(2),
                    firstCase?.compliance === false
                      ? 'NO OK'
                      : firstCase?.compliance === true
                      ? 'OK'
                      : 'NO evaluado',
                    firstCase?.compliance90 === 0
                      ? 'NO OK'
                      : firstCase?.compliance90 === 1
                      ? 'OK'
                      : 'NO evaluado',
                  ]),
            ],
          ],
          startY: 90,
        });
      };

      const addHeaderCaseTable = (caseData, index, innerIndex, caseNumber) => {
        const pageHeight = doc.internal.pageSize.getHeight();
        let startY =
          index === 0
            ? doc.lastAutoTable.finalY + 60
            : innerIndex === 0
            ? 60
            : doc.lastAutoTable
            ? doc.lastAutoTable.finalY + 20
            : 60;

        const minSpaceNeeded = 150;

        if (startY + minSpaceNeeded > pageHeight - 80) {
          doc.addPage();
          startY = 60;
        }

        let columns;
        if (caseNumber === '1') {
          columns = ['X̅', 'Delta', 'f(x̅)', 'fck / fck 90%'];
        } else if (caseNumber === '0') {
          columns = ['x(1)', 'fck / fck 90%'];
        } else if (normative === 'EHE' && caseNumber === '2') {
          columns = ['X̅', 'rn', 'K2', 'f(x)'];
        } else if (normative === 'EHE' && caseNumber === '3') {
          columns = ['x(1)', 'S35', 'K3', 'f(x(1))'];
        } else if (normative !== 'EHE' && caseNumber === '3') {
          columns = ['x(1)', 'kn', 'f(x(1))'];
        } else if (normative !== 'EHE' && caseNumber === '2') {
          columns = ['X̅', 'S35', 'f(x)'];
        } else {
          // Fallback por defecto
          columns = ['x(1)', 'S35', 'K3', 'f(x(1))'];
        }

        // Determinar valores según normativa y caso
        let bodyValues;
        if (caseNumber === '1') {
          bodyValues = [
            caseData?.ave_mash_strength?.toFixed(2) ||
              caseData?.min_ave_mash_strength?.toFixed(2),
            caseData.delivery_note_data[0].delta,
            caseData.acceptance_value?.toFixed(2),
            `${caseData?.concrete_value?.toFixed(2)} / ${(
              (caseData.concrete_value || 0) * 0.9
            ).toFixed(2)}`,
          ];
        } else if (caseNumber === '0') {
          bodyValues = [
            caseData?.min_ave_mash_strength?.toFixed(2),
            `${caseData?.concrete_value?.toFixed(2)} / ${(
              (caseData.concrete_value || 0) * 0.9
            ).toFixed(2)}`,
          ];
        } else if (normative === 'EHE' && caseNumber === '2') {
          bodyValues = [
            caseData.ave_mash_strength?.toFixed(2),
            caseData.rn_mash?.toFixed(2),
            caseData.k2_mash?.toFixed(2),
            caseData.result_mash?.toFixed(2),
          ];
        } else if (normative === 'EHE' && caseNumber === '3') {
          bodyValues = [
            caseData.min_ave_mash_strength?.toFixed(2),
            caseData.s35_mash?.toFixed(2),
            caseData.k3_mash?.toFixed(2),
            caseData.acceptance_value?.toFixed(2),
          ];
        } else if (normative !== 'EHE' && caseNumber === '3') {
          bodyValues = [
            caseData.min_ave_mash_strength?.toFixed(2),
            caseData.kn_mash?.toFixed(2),
            caseData.acceptance_value?.toFixed(2),
          ];
        } else if (normative !== 'EHE' && caseNumber === '2') {
          bodyValues = [
            caseData.ave_mash_strength?.toFixed(2),
            caseData.s35_mash?.toFixed(2),
            caseData.acceptance_value?.toFixed(2),
          ];
        } else {
          // Fallback por defecto
          bodyValues = [
            caseData.ave_mash_strength?.toFixed(2),
            caseData.s35_mash?.toFixed(2),
            caseData.k3_mash?.toFixed(2),
            caseData.acceptance_value?.toFixed(2),
          ];
        }

        doc.autoTable({
          pageBreak: 'avoid',
          head: [['Lote', 'Volumen (m3)', ...columns, 'Observación']],
          body: [
            [
              caseData.batch_code,
              caseData.batch_volume,
              ...bodyValues,
              caseNumber === '1' || caseNumber === '0'
                ? `${
                    caseData.compliance === true
                      ? 'OK'
                      : caseData.compliance === false
                      ? 'NO OK'
                      : 'NO evaluado'
                  } / ${
                    caseData.compliance90 === 1
                      ? 'OK'
                      : caseData.compliance90 === 0
                      ? 'NO OK'
                      : 'NO evaluado'
                  }`
                : caseData.compliance === true
                ? 'OK'
                : caseData.compliance === false
                ? 'NO OK'
                : '',
            ],
          ],
          ...commonTableStyles,
          startY,
        });
      };
      const generateCompressiveStrengthText = (note) => {
        const strengths = [];
        const labData = note.laboratory_note_data;

        if (!labData) return [];

        const standardDays = [3, 7, 28, 60];

        standardDays.forEach((day) => {
          for (let obs = 1; obs <= 3; obs++) {
            const key = `compressive_strength_${day}_${obs}`;
            const value = labData[key];
            if (value !== null && value !== undefined) {
              strengths.push({ day, obs, value });
            }
          }
        });

        if (labData.strength_extra) {
          Object.keys(labData.strength_extra).forEach((key) => {
            const match = key.match(/compressive_strength_(\d+)_(\d+)/);
            if (match) {
              const day = parseInt(match[1]);
              const obs = parseInt(match[2]);
              const value = labData.strength_extra[key];
              if (value !== null && value !== undefined) {
                strengths.push({ day, obs, value });
              }
            }
          });
        }

        strengths.sort((a, b) => {
          if (a.day !== b.day) return a.day - b.day;
          return a.obs - b.obs;
        });

        return strengths.map((s) => ({
          label: `${s.day}D ${s.obs}:`,
          value: s.value.toFixed(2),
        }));
      };

      const addCaseTable = (deliveryNoteData) => {
        const filteredData = onlyControlMash
          ? deliveryNoteData.filter((note) => note.mash_control === true)
          : deliveryNoteData;

        doc.autoTable({
          head: [
            [
              ...(type === '100' ? ['Lote', 'Volumen (m3)'] : []),
              'No Albarán',
              'Fecha',
              'Elemento estructural',
              'Situación',
              'Ref. Laboratorio',
              'Rotura',
              'Xi 28D',
              ...(type === 'statistical'
                ? ['Dispersión']
                : [
                    'Dispersión entre 2 probetas a 28 días',
                    'Dispersión entre 3 probetas a 28 días',
                  ]),
              'Observación',
            ],
          ],
          body: filteredData.map((note) => {
            const strengthData = generateCompressiveStrengthText(note);
            return [
              ...(type === '100' ? [note.batch_code, note.batch_volume] : []),
              note.docket_number,
              note.arriving_date,
              note.structural_element,
              note.detailed_location,
              note.laboratory_note_data?.reference_number,
              Array.isArray(strengthData) && strengthData.length > 0
                ? strengthData
                : '',
              (
                note.laboratory_note_data?.ave_strength_28_2 ??
                note.laboratory_note_data?.ave_strength_28_3
              )?.toFixed(2),
              ...(type === 'statistical'
                ? [
                    (
                      note.laboratory_note_data?.scatter_28_2 ??
                      note.laboratory_note_data?.scatter_28_3
                    )?.toFixed(2),
                  ]
                : [
                    note.laboratory_note_data?.scatter_28_2?.toFixed(2),
                    note.laboratory_note_data?.scatter_28_3?.toFixed(2),
                  ]),
              note.laboratory_note_data?.observation_28,
            ];
          }),

          ...commonTableStyles,
          startY: doc.lastAutoTable.finalY + 20,
          willDrawCell: (data) => {
            const columnIndex = type === '100' ? 7 : 5;
            if (data.column.index === columnIndex && data.section === 'body') {
              const strengthData = data.cell.raw;
              if (Array.isArray(strengthData) && strengthData.length > 0) {
                data.cell.text = [];
              }
            }
          },
          didDrawCell: (data) => {
            const columnIndex = type === '100' ? 7 : 5;
            if (data.column.index === columnIndex && data.section === 'body') {
              const strengthData = data.cell.raw;
              if (Array.isArray(strengthData) && strengthData.length > 0) {
                const cellWidth = data.cell.width;
                const maxLineWidth = cellWidth - 10; // Margen de 5px a cada lado
                const lineHeight = 10;

                // Agrupar elementos en líneas
                const lines = [];
                let currentLine = [];
                let currentLineWidth = 0;

                strengthData.forEach((s, idx) => {
                  doc.setFont('AccionaBold', 'bold');
                  doc.setFontSize(7);
                  const labelWidth = doc.getTextWidth(s.label) + 2;

                  doc.setFont('AccionaRegular', 'normal');
                  const valueWidth = doc.getTextWidth(s.value);
                  const separatorWidth =
                    idx < strengthData.length - 1
                      ? doc.getTextWidth('    ')
                      : 0;

                  const itemWidth = labelWidth + valueWidth + separatorWidth;

                  if (
                    currentLineWidth + itemWidth > maxLineWidth &&
                    currentLine.length > 0
                  ) {
                    // Nueva línea
                    lines.push([...currentLine]);
                    currentLine = [
                      { ...s, isLast: idx === strengthData.length - 1 },
                    ];
                    currentLineWidth = itemWidth;
                  } else {
                    currentLine.push({
                      ...s,
                      isLast: idx === strengthData.length - 1,
                    });
                    currentLineWidth += itemWidth;
                  }
                });

                if (currentLine.length > 0) {
                  lines.push(currentLine);
                }

                // Calcular posición inicial Y centrada
                const totalHeight = lines.length * lineHeight;
                let yPos =
                  data.cell.y +
                  (data.cell.height - totalHeight) / 2 +
                  lineHeight / 2;

                // Dibujar cada línea
                lines.forEach((line) => {
                  let lineWidth = 0;
                  line.forEach((s, idx) => {
                    doc.setFont('AccionaBold', 'bold');
                    doc.setFontSize(7);
                    lineWidth += doc.getTextWidth(s.label) + 2;

                    doc.setFont('AccionaRegular', 'normal');
                    lineWidth += doc.getTextWidth(s.value);

                    if (idx < line.length - 1) {
                      lineWidth += doc.getTextWidth('    ');
                    }
                  });

                  let xPos = data.cell.x + (cellWidth - lineWidth) / 2;

                  line.forEach((s, idx) => {
                    doc.setFont('AccionaBold', 'bold');
                    doc.setFontSize(7);
                    doc.setTextColor(0, 0, 0);
                    doc.text(s.label, xPos, yPos, { baseline: 'middle' });
                    xPos += doc.getTextWidth(s.label) + 2;

                    doc.setFont('AccionaRegular', 'normal');
                    doc.text(s.value, xPos, yPos, { baseline: 'middle' });
                    xPos += doc.getTextWidth(s.value);

                    if (idx < line.length - 1) {
                      xPos += doc.getTextWidth('    ');
                    }
                  });

                  yPos += lineHeight;
                });
              }
            }
          },
        });
      };

      const addCasesTables = () => {
        caseKeys.forEach((caseKey, index) => {
          if (index > 0) doc.addPage();
          const caseNumber = caseKey.split('case')[1];
          doc.setFontSize(12);
          doc.text(
            `Caso ${caseNumber}`,
            20,
            index === 0 ? doc.lastAutoTable.finalY + 50 : 50,
          );
          data[caseKey].forEach((caseData, innerIndex) => {
            addHeaderCaseTable(caseData, index, innerIndex, caseNumber);
            addCaseTable(caseData.delivery_note_data);
          });
        });
      };

      addDocumentHeader(doc, pageWidth);
      addTitleTable();
      if (type === 'statistical') {
        addCasesTables();
      } else {
        caseKeys.forEach((caseKey) => {
          addCaseTable(
            data[caseKey].reduce(
              (acc, batch) => [
                ...acc,
                ...batch.delivery_note_data.map((note) => ({
                  ...note,
                  batch_code: batch.batch_code,
                  batch_volume: batch.batch_volume,
                })),
              ],
              [],
            ),
          );
        });
      }

      const pageCount = doc.internal.getNumberOfPages();
      doc.setFont('AccionaRegular', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(128, 128, 128);

      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const pageText = `${i} de ${pageCount}`;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const textWidth = doc.getTextWidth(pageText);

        doc.text(pageText, pageWidth / 2 - textWidth / 2, pageHeight - 30);
      }

      doc.save(
        `${project.work_code}_${normative.toUpperCase()}_${
          concreteClass.name.split('CE.')[1] || 100
        }_${dayjs().format('YYYYMMDD')}.pdf`,
      );
      resolve();
    } catch (error) {
      console.error('Error generating Concrete Class PDF:', error);
      reject(error);
    }
  });
};

export { generateLG14PDF, generateConcreteClassPDF };
